import React, { useState } from 'react';
import { Button, Container, Table } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { Row } from 'reactstrap';
import KaAlert from '../Common/KaAlert';
import KaModal from '../Common/KaModal';
import BoldButton from '../Common/BoldButton';
import Confirm from '../Common/Confirm';
import QueryView from '../Common/QueryView';
import useDisplayMessage from '../Common/useDisplayMessage';
import BusinessAdminTemplate from '../UserAccess/BusinessAdminTemplate';
import useGetExternalIntegrations, { ExternalIntegrationDto } from '../../requests/useGetExternalIntegrations';
import useRemoveExternalIntegration from '../../requests/useRemoveExternalIntegration';
import { ExternalIntegrationRegisteredDto } from '../../requests/useRegisterExternalIntegration';
import { CreateEditExternalIntegration } from './CreateEditExternalIntegration';

enum States {
  None,
  CreateEdit,
  Registered,
}

type PopupState =
  { state: States.None; } |
  {
    state: States.CreateEdit;
    integration: ExternalIntegrationDto | null;
  } |
  {
    state: States.Registered;
    clientId: string;
    secret: string;
  };

interface RegisteredFormProps {
  visible: boolean;
  clientId: string;
  secret: string;
  onHide: () => void;
}

const RegisteredForm = (props: RegisteredFormProps) => {
  return (
    <KaModal
      title="External integration registered"
      onHide={props.onHide}
      show={props.visible}
      body={<>
        <p>The following client ID and secret are used by the external integration to authenticate.</p>
        <ul>
          <li>Client ID: {props.clientId}</li>
          <li>Secret: {props.secret}</li>
        </ul>
      </>} />);
};

const displayLabel = (integration: ExternalIntegrationDto) => integration.name || integration.clientId;

const alphabeticallyByName = (a: ExternalIntegrationDto, b: ExternalIntegrationDto) =>
  (a.name || '').localeCompare(b.name || '');

export const ExternalIntegrationsPage = () => {
  const displayMessage = useDisplayMessage();
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });
  const [integrationToRemove, setIntegrationToRemove] = useState<ExternalIntegrationDto | undefined>(undefined);

  const getExternalIntegrations = useGetExternalIntegrations();

  const remove = useRemoveExternalIntegration(integrationToRemove?.clientId ?? '', {
    onSuccess: () => {
      displayMessage.success(`External integration ${integrationToRemove && displayLabel(integrationToRemove)} removed.`);
      return getExternalIntegrations.query();
    },
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setIntegrationToRemove(undefined),
  });

  const registerClicked = () => {
    displayMessage.clear();
    setPopupState({ state: States.CreateEdit, integration: null });
  };

  const editClicked = (integration: ExternalIntegrationDto) => {
    displayMessage.clear();
    setPopupState({ state: States.CreateEdit, integration });
  };

  const registered = (result: ExternalIntegrationRegisteredDto) => {
    setPopupState({ state: States.Registered, clientId: result.clientId, secret: result.secret });
    getExternalIntegrations.query();
  };

  const edited = (name: string) => {
    setPopupState({ state: States.None });
    displayMessage.success(`External integration ${name} updated.`);
    getExternalIntegrations.query();
  };

  const dismissModal = () => setPopupState({ state: States.None });

  return <BusinessAdminTemplate>
    <Container>
      {popupState.state === States.CreateEdit &&
        <CreateEditExternalIntegration
          initial={popupState.integration}
          onDismiss={dismissModal}
          onRegistered={registered}
          onEdited={edited} />}
      <RegisteredForm
        visible={popupState.state === States.Registered}
        clientId={popupState.state === States.Registered ? popupState.clientId : ''}
        secret={popupState.state === States.Registered ? popupState.secret : ''}
        onHide={dismissModal} />
      <Confirm
        visible={!!integrationToRemove}
        title='Remove External Integration'
        body={`Are you sure that you want to permanently remove ${integrationToRemove ? displayLabel(integrationToRemove) : ''}?`}
        isLoading={remove.isLoading}
        onDismiss={() => setIntegrationToRemove(undefined)}
        onConfirm={() => remove.request()} />
      <Row className="justify-content-between mb-2">
        <h2 className="col-auto ka-blue">External Integrations</h2>
        <BoldButton className="col-auto btn-ghost-primary" onClick={registerClicked}>Register Integration</BoldButton>
      </Row>
      <p><a href="./docs/extapi/index.html" target="_blank">API Documentation</a></p>
      <p><NavLink to="/apiEventLog">View API Event Log</NavLink></p>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <QueryView
        query={getExternalIntegrations}
        renderData={integrations =>
          <Table bordered>
            <thead>
              <tr>
                <th>Name</th>
                <th>Client ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...integrations].sort(alphabeticallyByName).map(integration =>
                <tr key={integration.clientId}>
                  <td>{integration.name || '—'}</td>
                  <td>{integration.clientId}</td>
                  <td align="right">
                    <div className='dropdown'>
                      <Button id='actionsMenuButton'
                        className='dropdown-toggle btn-ghost-secondary'
                        variant='link'
                        data-bs-toggle='dropdown'
                        aria-expanded='false'><strong>Actions</strong></Button>
                      <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                        <BoldButton
                          className='dropdown-item btn-ghost-secondary'
                          onClick={() => editClicked(integration)}>Edit</BoldButton>
                        <BoldButton
                          className='dropdown-item btn-ghost-secondary'
                          onClick={() => setIntegrationToRemove(integration)}>Remove</BoldButton>
                      </div>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </Table>} />
    </Container>
  </BusinessAdminTemplate>;
}
